interface EvaluationContext {
    [key: string]: any; // Allows any key-value pairs for context variables
  }
  
  export class ConditionalElement {
    private element: HTMLElement;
    private expression: string;
    private context: EvaluationContext;
  
    constructor(element: HTMLElement) {
      this.element = element;
      this.expression = element.getAttribute('data-expression') || '';
      const contextString = element.getAttribute('data-context') || '{}';
      this.context = JSON.parse(contextString) as EvaluationContext;
      this.evaluateAndDisplay();
    }
  
    private evaluateAndDisplay(): void {
      const shouldDisplay = this.evaluateExpression();
      if (shouldDisplay) {
        this.element.style.display = '';
      } else {
        this.element.style.display = 'none';
      }
    }
  
    private evaluateExpression(): boolean {
      if (!this.expression) {
        return true; // Or false, depending on your default behavior if no expression is provided
      }
      try {
        return this.parseAndEvaluate(this.expression, this.context);
      } catch (error) {
        console.error('Error evaluating expression:', error);
        return false; // Or handle the error as needed (e.g., hide the element)
      }
    }
    private parseAndEvaluate(expression: string, context: EvaluationContext): boolean {
      // Tokenize the expression
      const tokens = this.tokenize(expression);
  
      // Parse and evaluate the tokens
      const result = this.evaluateTokens(tokens, context);
      return result;
    }
  
    private tokenize(expression: string): string[] {
      // Basic tokenizer (can be improved for more complex expressions)
      return expression.replace(/\s+/g, '').match(/([a-zA-Z0-9._]+|==|!=|<=|>=|<|>|&&|\|\||\(|\)|true|false)/g) || [];
    }
  
  
    private evaluateTokens(tokens: string[], context: EvaluationContext): boolean {
      if (tokens.length === 0) {
          return true; // Or false, or throw an error, depending on your design
      }
  
      // A simple recursive descent parser
  
      let index = 0;
  
      function parseValue(): any {
          const token = tokens[index];
          if (token === undefined) {
              throw new Error("Unexpected end of expression");
          }
  
          if (token === 'true') {
              index++;
              return true;
          }
          if (token === 'false') {
              index++;
              return false;
          }
  
          if (context.hasOwnProperty(token)) {
              index++;
              return context[token];
          }
  
          if (!isNaN(Number(token))) { // Check for numeric literals
              index++;
              return Number(token);
          }
  
          throw new Error(`Unexpected token: ${token}`);
      }
  
  
      function parseComparison(): any {
          let left = parseValue();
          while (index < tokens.length) {
              const operator = tokens[index];
  
              if (['==', '!=', '<', '>', '<=', '>='].includes(operator)) {
                  index++;
                  const right = parseValue();
                  switch (operator) {
                      case '==':
                          left = left == right;
                          break;
                      case '!=':
                          left = left != right;
                          break;
                      case '<':
                          left = left < right;
                          break;
                      case '>':
                          left = left > right;
                          break;
                      case '<=':
                          left = left <= right;
                          break;
                      case '>=':
                          left = left >= right;
                          break;
                  }
              } else {
                  break; // Not a comparison operator, so break the loop
              }
          }
          return left;
      }
  
      function parseLogicalOr(): any {
          let left = parseComparison();
          while (index < tokens.length && tokens[index] === '||') {
              index++;
              const right = parseComparison();
              left = left || right;
          }
          return left;
      }
  
  
      function parseLogicalAnd(): any {
          let left = parseLogicalOr();
          while (index < tokens.length && tokens[index] === '&&') {
              index++;
              const right = parseLogicalOr();
              left = left && right;
          }
          return left;
      }
  
      const result = parseLogicalAnd();
  
      if (index < tokens.length) {
          throw new Error(`Unexpected token at the end: ${tokens.slice(index).join(' ')}`);
      }
  
      return result;
    }
  }